import { NgModule, provideBrowserGlobalErrorListeners } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { App } from './app';
import { appRoutes } from './app.routes';

import { HttpClientModule, provideHttpClient } from '@angular/common/http';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_TOOLTIP_DEFAULT_OPTIONS } from '@angular/material/tooltip';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  MdsFormlyAdvancedAutocompleteComponent,
  MdsFormlyAttachmentComponent,
  MdsFormlyAttachmentsFieldComponent,
  MdsFormlyAttachmentsWrapperComponent,
  MdsFormlyAutocompleteComponent,
  MdsFormlyAutocompleteV2Component,
  MdsFormlyCheckboxComponent,
  MdsFormlyCheckboxGroupFilterComponent,
  MdsFormlyColorComponent,
  MdsFormlyDatepickerComponent,
  MdsFormlyFileDropComponent,
  MdsFormlyFormulaComponent,
  MdsFormlyInputComponent,
  MdsFormlyMultiCheckboxComponent,
  MdsFormlyMultiSelectComponent,
  MdsFormlyNumericCheckComponent,
  MdsFormlyNumericUnitComponent,
  MdsFormlyPanelComponent,
  MdsFormlyRadioComponent,
  MdsFormlyReadonlyComponent,
  MdsFormlySelectChoiceComponent,
  MdsFormlySelectComponent,
  MdsFormlySelectGroupFilterComponent,
  MdsFormlySliderComponent,
  MdsFormlyTagsComponent,
  MdsFormlyTextareaComponent,
  MdsFormlyTextCheckComponent,
  MdsFormlyTileAutocompleteComponent,
  MdsFormlyUrlComponent,
  MdsFormlyUrlShowOnlyComponent,
  MdsFormlyVerticalPanelComponent
} from '@mds/ui';
import { ConfiguratorService } from '@pages/configurator/configurator.service';
import { AnalyzeLayersService } from '@services/analyze.layers.service';
import { AnalyzeService } from '@services/analyze.service';
import { AuthService } from '@services/auth.service';
import { ComposerService } from '@services/composer.service';
import { CookiesService } from '@services/cookies.service';
import { CostingService } from '@services/costing.service';
import { EPDManagementService } from '@services/epd-management.service';
import { FAQService } from '@services/FAQ.service';
import { GlobalDialogService } from '@services/global-dialog.service';
import { HostnameService } from '@services/hostname.service';
import { InlineManualService } from '@services/inline-manual.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MakersiteService } from '@services/makersite.service';
import { NewObjectService } from '@services/new-object.service';
import { RouterHelperService } from '@services/router-helper.service';
import { SessionTimeoutService } from '@services/session-timeout.service';
import { TasksService } from '@services/tasks.service';
import { TaxonomyService } from '@services/taxonomy.service';
import { ToastrService } from '@services/toaster.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { MenuService } from 'ag-grid-community';
import { httpInterceptorProviders } from './interceptors';
import { tooltipDefaults } from './layout/layout.module';

export function EmailValidatorMessage(err, field: FormlyFieldConfig) {
  return 'Please enter valid email id';
}

@NgModule({
  declarations: [App],
  imports: [
    HttpClientModule,
    BrowserModule,
    RouterModule.forRoot(appRoutes),
    FormlyModule.forRoot({
      validationMessages: [
        { name: 'required', message: 'Required field' },
        { name: 'pattern', message: 'Please enter a valid value' },
        { name: 'min', message: 'Please enter a valid value' },
        { name: 'max', message: 'Please enter a valid value' },
        { name: 'email', message: EmailValidatorMessage }
      ],
      types: [
        { name: 'mds-formly-input', component: MdsFormlyInputComponent },
        { name: 'mds-formly-textarea', component: MdsFormlyTextareaComponent },
        { name: 'mds-formly-select', component: MdsFormlySelectComponent },
        { name: 'mds-formly-radio', component: MdsFormlyRadioComponent },
        { name: 'mds-formly-readonly', component: MdsFormlyReadonlyComponent },
        { name: 'mds-formly-attachment', component: MdsFormlyAttachmentComponent },
        { name: 'mds-formly-datepicker', component: MdsFormlyDatepickerComponent },
        { name: 'mds-formly-checkbox', component: MdsFormlyCheckboxComponent },
        { name: 'mds-formly-autocomplete', component: MdsFormlyAutocompleteComponent },
        { name: 'mds-formly-checkbox-group-filter', component: MdsFormlyCheckboxGroupFilterComponent },
        { name: 'mds-formly-select-group-filter', component: MdsFormlySelectGroupFilterComponent },

        { name: 'mds-formly-multi-checkbox', component: MdsFormlyMultiCheckboxComponent },
        { name: 'mds-formly-multi-select', component: MdsFormlyMultiSelectComponent },
        { name: 'mds-formly-select-choice', component: MdsFormlySelectChoiceComponent },
        { name: 'mds-formly-numeric-unit', component: MdsFormlyNumericUnitComponent },
        { name: 'mds-formly-tags', component: MdsFormlyTagsComponent },
        { name: 'mds-formly-textarea', component: MdsFormlyTextareaComponent },
        { name: 'mds-formly-formula', component: MdsFormlyFormulaComponent },
        { name: 'mds-formly-advanced-autocomplete', component: MdsFormlyAdvancedAutocompleteComponent },
        { name: 'mds-formly-tile-autocomplete', component: MdsFormlyTileAutocompleteComponent },
        { name: 'mds-formly-numeric-check', component: MdsFormlyNumericCheckComponent },
        { name: 'mds-formly-text-check', component: MdsFormlyTextCheckComponent },
        { name: 'mds-formly-slider', component: MdsFormlySliderComponent },
        { name: 'mds-formly-url-show-only', component: MdsFormlyUrlShowOnlyComponent },
        { name: 'mds-formly-url', component: MdsFormlyUrlComponent },
        { name: 'mds-formly-file-drop', component: MdsFormlyFileDropComponent },
        { name: 'mds-formly-autocomplete-v2', component: MdsFormlyAutocompleteV2Component },
        { name: 'mds-formly-color', component: MdsFormlyColorComponent }
      ],
      wrappers: [
        { name: 'mds-panel', component: MdsFormlyPanelComponent },
        { name: 'vertical-panel', component: MdsFormlyVerticalPanelComponent },
        { name: 'mds-attachments-field', component: MdsFormlyAttachmentsFieldComponent },
        { name: 'mds-attachments', component: MdsFormlyAttachmentsWrapperComponent }
      ],
      extras: { lazyRender: true }
    }),
    FormlyMaterialModule
  ],
  providers: [
    // {
    //   provide: ErrorHandler,
    //   useValue: Sentry.createErrorHandler(),
    // },
    LocalStorageService,
    AuthService,
    CookiesService,
    CostingService,
    EPDManagementService,
    HostnameService,
    ToastrService,
    MakersiteService,
    AnalyzeService,
    MenuService,
    FAQService,
    TasksService,
    TaxonomyService,
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaults },
    httpInterceptorProviders,
    InlineManualService,
    ToolbarEventService,
    ComposerService,
    RouterHelperService,
    SessionTimeoutService,
    AnalyzeLayersService,
    GlobalDialogService,
    NewObjectService,
    ConfiguratorService,
    provideBrowserGlobalErrorListeners(),
    provideAnimations(),
    provideHttpClient(),
    provideNativeDateAdapter()
  ],
  bootstrap: [App]
})
export class AppModule {}
